<template>
    <div class="absolute top-0 w-full">
        <div class="container mb-28 flex flex-col justify-self-center pt-34">
            <div class="col-span-full !col-start-1 flex flex-col gap-2">
                <div class="view-h1 text-dark-blue">{{ t('title') }}</div>
                <div class="view-h3">{{ t('subtitle') }}</div>
            </div>

            <div class="col-span-full mt-12 flex flex-col gap-8">
                <div class="flex w-full flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                    <div class="flex flex-wrap items-center gap-2">
                        <span>
                            {{ t('themes-intro') }}
                        </span>
                        <FilterGroup
                            v-if="themes.length"
                            design="as-sentence"
                            :label="t('themes')"
                            :items="themes"
                            :selected-slugs="selectedThemeSlugs"
                            single-select
                            :locale="mainStore.active_locale"
                            @change="slugs => updateFilterQuery('theme', slugs)"
                        />
                    </div>
<!--                    <FilterGroup-->
<!--                        v-if="projectTypes.length"-->
<!--                        design="dropdown"-->
<!--                        :label="t('projectTypes')"-->
<!--                        :show-all-label="t('showAllProjectTypes')"-->
<!--                        :show-all-count="showAllProjectTypesCount"-->
<!--                        :items="projectTypes"-->
<!--                        :option-counts="projectTypeCounts"-->
<!--                        :selected-slug="selectedProjectTypeSlug"-->
<!--                        :locale="mainStore.active_locale"-->
<!--                        @select="selectProjectType"-->
<!--                    />-->
                </div>

                <template v-if="filteredCases.length">
                    <div id="work-results" class="grid w-full grid-cols-1 gap-x-4 gap-y-16 md:grid-cols-12">
                        <CaseItem
                            v-for="(item, index) in visibleCases"
                            :key="item.contentId"
                            :size="caseItemSize(index)"
                            :content-id="item.contentId"
                            :slug="item.data.slug"
                            :image="item.data['hero-image']"
                            :title="localizedText(item.data.title)"
                            :credit="localizedText(item.data.credits?.client_and_location)"
                            :class="{ 'md:hidden': index >= desktopVisibleCasesCount }"
                        />
                    </div>
                    <button
                        v-if="hasMoreMobileCases"
                        type="button"
                        class="self-center rounded-full border border-dark-blue bg-background px-5 py-2 text-sm text-text transition-colors hover:bg-dark-blue hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-blue focus-visible:ring-offset-2 md:hidden"
                        aria-controls="work-results"
                        @click="loadMoreCases"
                    >
                        {{ t('loadMore') }}
                    </button>
                    <button
                        v-if="hasMoreDesktopCases"
                        type="button"
                        class="hidden self-center rounded-full border border-dark-blue bg-background px-5 py-2 text-sm text-text transition-colors hover:bg-dark-blue hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-blue focus-visible:ring-offset-2 md:block"
                        aria-controls="work-results"
                        @click="loadMoreCases"
                    >
                        {{ t('loadMore') }}
                    </button>
                </template>
                <div v-else class="py-20 text-center view-h3">
                    {{ t('noResults') }}
                </div>
            </div>
        </div>
        <Footer />
    </div>
</template>

<script setup lang="ts">
import type { ApiResponse } from '#layers/cms/shared/types/ApiResponse'
import type { WorkCase, WorkPageData } from '#shared/types/WorkPage'
import { Pages } from '#shared/types/Pages'
import CaseItem, { CaseItemWidth } from '~/components/view/CaseItem.vue'
import FilterGroup from '~/components/view/work/FilterGroup.vue'
import Footer from '~/components/view/Footer.vue'
import { filterWorkCases, parseFilterQuery, resolveTaxonomyIds } from '~/utils/workFilters'

const mainStore = useMainStore()
const siteStore = useSiteStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n({useScope: 'local'})
const localePath = useLocalePath()
const config = useRuntimeConfig()

const CASES_AMOUNT = 12

const siteUrl = String(config.public.publicHost || 'https://yipp.nl').replace(/\/$/, '')
const canonicalUrl = computed(() => `${siteUrl}${localePath('/work', locale.value)}`)

useHead(() => ({
    title: t('seo.title')
}))
useSeoMeta({
    description: () => t('seo.description'),
    ogTitle: () => t('seo.title'),
    ogDescription: () => t('seo.description'),
    ogUrl: () => canonicalUrl.value
})

definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: () => {
            if (typeof window !== 'undefined') window.scroll({ top: 0 })
        },
    },
})

const { data: response } = await useFetch<ApiResponse<WorkPageData>>('/api/work')

if (!response.value?.success) {
    throw createError({
        statusCode: 500,
        statusMessage: response.value?.message ?? 'Could not load work page data',
    })
}

const workData = computed(() => response.value?.success ? response.value.data : null)
const cases = computed(() => workData.value?.cases ?? [])
const projectTypes = computed(() => workData.value?.projectTypes ?? [])
const themes = computed(() => workData.value?.themes ?? [])
const overviewCaseIds = computed(() => workData.value?.overviewCaseIds ?? [])

const orderedCases = computed(() => {
    const casesById = new Map(cases.value.map(item => [item.contentId, item]))
    const overviewCases = overviewCaseIds.value
        .map(id => casesById.get(id))
        .filter((item): item is WorkCase => Boolean(item))
    const overviewIds = new Set(overviewCases.map(item => item.contentId))
    const remainingCases = cases.value.filter(item => !overviewIds.has(item.contentId))
    return [...overviewCases, ...remainingCases]
})

const requestedProjectTypeSlugs = computed(() => parseFilterQuery(route.query.type))
const requestedThemeSlugs = computed(() => parseFilterQuery(route.query.theme))

const selectedProjectTypeSlugs = computed(() => {
    const validSlugs = new Set(projectTypes.value.map(item => item.slug))
    const selectedSlug = requestedProjectTypeSlugs.value.find(slug => validSlugs.has(slug))
    return selectedSlug ? [selectedSlug] : []
})

const selectedProjectTypeSlug = computed(() => selectedProjectTypeSlugs.value[0] ?? null)

const selectedThemeSlugs = computed(() => {
    const validSlugs = new Set(themes.value.map(item => item.slug))
    const selectedSlug = requestedThemeSlugs.value.find(slug => validSlugs.has(slug))
    return selectedSlug ? [selectedSlug] : []
})

const selectedProjectTypeIds = computed(() => {
    return resolveTaxonomyIds(selectedProjectTypeSlugs.value, projectTypes.value)
})

const selectedThemeIds = computed(() => {
    return resolveTaxonomyIds(selectedThemeSlugs.value, themes.value)
})

const showAllProjectTypesCount = computed(() => (
    filterWorkCases(cases.value, [], selectedThemeIds.value).length
))

const projectTypeCounts = computed<Record<string, number>>(() => (
    Object.fromEntries(projectTypes.value.map(item => [
        item.slug,
        filterWorkCases(cases.value, [item.contentId], selectedThemeIds.value).length,
    ]))
))

const hasActiveFilters = computed(() => {
    return selectedProjectTypeSlugs.value.length > 0 || selectedThemeSlugs.value.length > 0
})

const filteredCases = computed(() => {
    const source = hasActiveFilters.value ? cases.value : orderedCases.value
    return filterWorkCases(source, selectedProjectTypeIds.value, selectedThemeIds.value)
})

const UNFILTERED_CASE_SIZES = [
    CaseItemWidth.full,
    CaseItemWidth.big,
    CaseItemWidth.small,
    CaseItemWidth.small,
    CaseItemWidth.small,
    CaseItemWidth.small,
    CaseItemWidth.small,
    CaseItemWidth.big,
    CaseItemWidth.big,
    CaseItemWidth.small,
] as const

const loadedCasePages = ref(0)
const mobileVisibleCasesCount = computed(() => CASES_AMOUNT * (loadedCasePages.value + 1))
const desktopVisibleCasesCount = computed(() => (
    (hasActiveFilters.value ? CASES_AMOUNT : 10) + (CASES_AMOUNT * loadedCasePages.value)
))
const visibleCases = computed(() => filteredCases.value.slice(0, mobileVisibleCasesCount.value))
const hasMoreMobileCases = computed(() => mobileVisibleCasesCount.value < filteredCases.value.length)
const hasMoreDesktopCases = computed(() => desktopVisibleCasesCount.value < filteredCases.value.length)

const caseItemSize = (index: number): CaseItemWidth => {
    if (hasActiveFilters.value) return CaseItemWidth.small
    return UNFILTERED_CASE_SIZES[index] ?? CaseItemWidth.small
}

const loadMoreCases = () => {
    loadedCasePages.value += 1
}

watch(
    [selectedProjectTypeSlug, () => selectedThemeSlugs.value[0]],
    () => {
        loadedCasePages.value = 0
    },
)

const updateFilterQuery = async (key: 'type' | 'theme', slugs: string[]) => {
    const query = { ...route.query }
    if (slugs.length) query[key] = slugs.join(',')
    else delete query[key]
    await router.push({ query })
}

const selectProjectType = async (slug: string | null) => {
    await updateFilterQuery('type', slug ? [slug] : [])
}

const clearFilters = async () => {
    const query = { ...route.query }
    delete query.type
    delete query.theme
    await router.push({ query })
}

const localizedText = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (!value || typeof value !== 'object' || Array.isArray(value)) return ''
    const localized = value as Record<string, unknown>
    const activeValue = localized[mainStore.active_locale]
    if (typeof activeValue === 'string') return activeValue
    if (typeof localized.en_GB === 'string') return localized.en_GB
    const fallback = Object.values(localized).find(value => typeof value === 'string')
    return typeof fallback === 'string' ? fallback : ''
}

onMounted(() => {
    siteStore.setCurrentPage(Pages.WORK)
    siteStore.setIconColor('dark')
})
</script>

<i18n lang="yaml">
en:
    title: Our Work
    subtitle: Selected Projects
    projectTypes: Project type
    showAllProjectTypes: Show All Project Types
    themes-intro: We create experiences about
    themes: Theme
    clearFilters: Clear filters
    noResults: No projects match these filters.
    loadMore: Load More
    seo:
        title: "Our Work — Case Studies & Projects"
        description: "Explore YIPP's portfolio of digital exhibitions, interactive installations, and immersive experiences for museums, brands, and public spaces."
nl:
    title: Projecten
    subtitle: Uitgelichte Projecten
    projectTypes: Projecttype
    showAllProjectTypes: Toon alle projecttypen
    themes-intro: Wij maken ervaringen over
    themes: Thema
    clearFilters: Filters wissen
    noResults: Geen projecten komen overeen met deze filters.
    loadMore: Meer laden
    seo:
        title: "Ons Werk — Cases & Projecten"
        description: "Bekijk het portfolio van YIPP: digitale tentoonstellingen, interactieve installaties en meeslepende ervaringen voor musea, merken en publieke ruimtes."
</i18n>
